import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

// Original routed-line geometry. Fine strokes and travelling dashes, not a copied SVG/video.
const curve = (a, b, c, d, t) => {
  const s = 1 - t;
  return [s*s*s*a[0]+3*s*s*t*b[0]+3*s*t*t*c[0]+t*t*t*d[0], s*s*s*a[1]+3*s*s*t*b[1]+3*s*t*t*c[1]+t*t*t*d[1]];
};
const makeRoute = (width, height, index) => {
  const y = .42 + index*.078, bottom = .87 + index*.027, bend = .13 + index*.025;
  const segments = [
    [[-.06,y],[0,y],[bend*.5,y],[bend,y]],
    [[bend,y],[bend+.10,y],[bend+.10,bottom],[.36,bottom]],
    [[.36,bottom],[.44,bottom],[.56,bottom],[.64,bottom]],
    [[.64,bottom],[1-bend-.10,bottom],[1-bend-.10,y],[1-bend,y]],
    [[1-bend,y],[1-bend*.5,y],[1,y],[1.06,y]],
  ];
  return segments.flatMap(s => Array.from({ length:48 }, (_,i) => {
    const [x,y2] = curve(...s,i/47); return [x*width,y2*height];
  }));
};

export const HeroConnections = ({ paused }) => {
  const canvas = useRef(null);
  const phase = useRef(0);
  const reduced = useReducedMotion();
  useEffect(() => {
    const el = canvas.current, ctx = el?.getContext("2d");
    if (!ctx) return undefined;
    let frame = 0, last = 0, elapsed = phase.current, visible = true, width = 0, height = 0, routes = [];
    const draw = () => {
      ctx.clearRect(0,0,width,height);
      ctx.lineWidth = 1; ctx.lineCap = "round";
      routes.forEach((points,index) => {
        ctx.strokeStyle = index === 0 ? "rgba(238,117,1,.25)" : "rgba(220,219,212,.19)";
        ctx.beginPath(); points.forEach(([x,y],i) => i ? ctx.lineTo(x,y) : ctx.moveTo(x,y)); ctx.stroke();
        if (reduced) return;
        [0,.5].forEach(offset => {
          const fraction = ((elapsed/(14500+index*1200)) + index*.22 + offset)%1;
          const start = Math.floor(fraction*(points.length-9));
          ctx.strokeStyle = index%2 ? "rgba(230,230,224,.8)" : "rgba(238,117,1,.95)";
          ctx.lineWidth = 2;
          ctx.beginPath(); points.slice(start,start+6).forEach(([x,y],i)=>i?ctx.lineTo(x,y):ctx.moveTo(x,y));ctx.stroke();
          ctx.lineWidth=1;
        });
      });
    };
    const tick = (now) => {
      if (last && now-last < 1000/30) { frame=requestAnimationFrame(tick);return; }
      if (last) elapsed += Math.min(now-last,80);
      last=now;draw();frame=requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);last=0;
      const active = visible && !paused && !reduced && !document.hidden;
      el.dataset.animating = String(active);
      if(active) frame=requestAnimationFrame(tick); else draw();
    };
    const resize = () => {
      const box=el.getBoundingClientRect();width=box.width;height=box.height;
      const ratio=Math.min(window.devicePixelRatio||1,2);
      el.width=Math.round(width*ratio);el.height=Math.round(height*ratio);ctx.setTransform(ratio,0,0,ratio,0,0);
      routes=Array.from({length:width<640?2:4},(_,i)=>makeRoute(width,height,i));draw();
    };
    const ro=new ResizeObserver(resize);ro.observe(el);
    const io=new IntersectionObserver(([entry])=>{visible=entry.isIntersecting;sync();});io.observe(el);
    document.addEventListener("visibilitychange",sync);resize();sync();
    return ()=>{phase.current=elapsed;cancelAnimationFrame(frame);ro.disconnect();io.disconnect();document.removeEventListener("visibilitychange",sync);};
  },[paused,reduced]);
  return <canvas ref={canvas} className="mk-hero-connections" aria-hidden="true" data-testid="hero-connections" />;
};